import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { X } from 'lucide-react';
import { ProfileData, fitnessGoals } from '../types/profileTypes';
import { WeightInput } from '@/components/measurement/WeightInput';
import { HeightInput } from '@/components/measurement/HeightInput';
import { useMeasurementPreferences } from '@/hooks/useMeasurementPreferences';

interface ProfileFormFieldsProps {
  formData: ProfileData;
  onFormDataChange: (updates: Partial<ProfileData>) => void;
  onAddGoal: (goal: string) => void;
  onRemoveGoal: (goal: string) => void;
}

export const ProfileFormFields: React.FC<ProfileFormFieldsProps> = ({
  formData,
  onFormDataChange,
  onAddGoal,
  onRemoveGoal,
}) => {
  const { preferences } = useMeasurementPreferences();
  return (
    <>
      {/* Basic Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Basic Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="first-name">First Name</Label>
            <Input
              id="first-name"
              value={formData.first_name}
              onChange={(e) => onFormDataChange({ first_name: e.target.value })}
              placeholder="Enter your first name"
            />
          </div>
          <div>
            <Label htmlFor="last-name">Last Name</Label>
            <Input
              id="last-name"
              value={formData.last_name}
              onChange={(e) => onFormDataChange({ last_name: e.target.value })}
              placeholder="Enter your last name"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="age">Age</Label>
            <Input
              id="age"
              type="number"
              value={formData.age || ''}
              onChange={(e) => onFormDataChange({ age: e.target.value ? Number(e.target.value) : null })}
              placeholder="Your age"
            />
          </div>
          <div>
            <Label htmlFor="height">Height</Label>
            <HeightInput
              label={undefined}
              value={formData.height_cm || 0}
              onChange={(cm) => onFormDataChange({ height_cm: cm && cm > 0 ? cm : null })}
              allowUnitChange
              placeholder="Enter your height"
            />
          </div>
        </div>
      </div>

      {/* Fitness Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Fitness Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="current-weight">Current Weight</Label>
            <WeightInput
              label={undefined}
              value={formData.current_weight_kg || 0}
              unit={preferences.weight_unit}
              onChange={(kg) => onFormDataChange({ current_weight_kg: kg && kg > 0 ? kg : null })}
              allowUnitChange
              placeholder="Enter your current weight"
            />
          </div>
          <div>
            <Label htmlFor="goal-weight">Goal Weight</Label>
            <WeightInput
              label={undefined}
              value={formData.weight_goal_kg || 0}
              unit={preferences.weight_unit}
              onChange={(kg) => onFormDataChange({ weight_goal_kg: kg && kg > 0 ? kg : null })}
              allowUnitChange
              placeholder="Enter your target weight"
            />
          </div>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="fitness-level">Fitness Level</Label>
            <Select 
              value={formData.fitness_level} 
              onValueChange={(value: 'beginner' | 'intermediate' | 'advanced') => 
                onFormDataChange({ fitness_level: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="beginner">Beginner</SelectItem>
                <SelectItem value="intermediate">Intermediate</SelectItem>
                <SelectItem value="advanced">Advanced</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="experience-level">Experience Level</Label>
            <Select 
              value={formData.experience_level} 
              onValueChange={(value: 'new' | 'returning' | 'experienced') => 
                onFormDataChange({ experience_level: value })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="new">New to Fitness</SelectItem>
                <SelectItem value="returning">Returning to Fitness</SelectItem>
                <SelectItem value="experienced">Experienced</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        
        {/* Primary Goals */}
        <div>
          <Label>Primary Goals</Label>
          <div className="space-y-2">
            <Select onValueChange={onAddGoal}>
              <SelectTrigger>
                <SelectValue placeholder="Add a fitness goal" />
              </SelectTrigger>
              <SelectContent>
                {fitnessGoals.map(goal => (
                  <SelectItem key={goal} value={goal}>{goal}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="flex flex-wrap gap-2">
              {formData.primary_goals.map(goal => (
                <Badge key={goal} variant="secondary" className="flex items-center gap-1">
                  {goal}
                  <X 
                    className="h-3 w-3 cursor-pointer" 
                    onClick={() => onRemoveGoal(goal)}
                  />
                </Badge>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Contact Information */}
      <div className="space-y-4">
        <h3 className="text-lg font-medium">Contact Information</h3>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              value={formData.phone}
              onChange={(e) => onFormDataChange({ phone: e.target.value })}
              placeholder="Your phone number"
            />
          </div>
          <div>
            <Label htmlFor="emergency-contact">Emergency Contact</Label>
            <Input
              id="emergency-contact"
              value={formData.emergency_contact}
              onChange={(e) => onFormDataChange({ emergency_contact: e.target.value })}
              placeholder="Emergency contact info"
            />
          </div>
        </div>
      </div>
    </>
  );
};